/**
 * Council settings, read-only (`/admin/settings`) — docs/08 §3.11.
 *
 * The *editor* lives on `/council`, next to the seats it governs: docs/08 §3.11
 * puts it there because a threshold only means something against the number of
 * seats actually filled, and the impact copy has to be able to say so. This page
 * stays as the plain reading of the singleton, bootstrap status included.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClaimsPanel } from './ClaimsPanel.js';
import { settingsApi } from '../../api/client.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';

export function AdminSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });

  if (settings.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (settings.isError) return <p className={ui.problem}>{problemMessage(settings.error, t)}</p>;

  const view = settings.data.settings;
  const rows: Array<[string, string]> = [
    [t('admin.councilSize'), String(view.council_size)],
    [t('admin.approvalThreshold'), String(view.approval_threshold)],
    [t('admin.votingWindow'), t('admin.days', { count: view.voting_window_days })],
    [t('admin.activeSeats'), `${view.active_seats} / ${view.council_size}`],
    [t('admin.bootstrapMode'), view.bootstrap_mode ? t('app.yes') : t('app.no')],
    [t('admin.graphVersion'), String(view.graph_version)],
  ];

  return (
    <div>
      <ClaimsPanel />

      <h1>{t('admin.settingsTitle')}</h1>

      {view.bootstrap_mode && (
        <div className={ui.notice} role="status">
          <strong>{t('home.bootstrapTitle')}</strong>
          <p style={{ margin: 'var(--space-2) 0 0' }}>
            {t('admin.bootstrapExplain', {
              granted: view.active_seats,
              threshold: view.approval_threshold,
            })}
          </p>
        </div>
      )}

      <section className={ui.card} style={{ marginBlockStart: 'var(--space-5)' }}>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'var(--space-2) var(--space-5)',
          }}
        >
          {rows.map(([label, value]) => (
            <div key={label} style={{ display: 'contents' }}>
              <dt className={ui.muted}>{label}</dt>
              <dd className="tabular" style={{ margin: 0 }}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
        <p className={ui.muted} style={{ marginBlockEnd: 0 }}>
          {t('admin.editorOnCouncil')} <Link to="/council">{t('nav.council')}</Link>
        </p>
      </section>
    </div>
  );
}
